import { ref, shallowRef, useId } from 'vue';
import { defineStore } from 'pinia';
import { useTraitsStore } from '@nuxt-common/media-card-large/app/stores/traitsStore';
import usePageDataStore from '~/stores/usePageData';

const useIsolatedStickyAtbStateStore = defineStore('isolatedStickyAtbStateStore', () => {
  const pageData = usePageDataStore().pageData;
  const traitsStore = useTraitsStore();
  const stickyAtbUUID = `sticky-atb-${useId()}`;
  const stickyAtbProductId = `${pageData?.productId}--${stickyAtbUUID}`;
  const pageDataTraits = pageData?.traits ?? {};
  const pageDataFlags = pageData?.flags?.groupByProducts ?? {};
  const forcedStickyRef = ref<number>(-Infinity);
  const pageDataStickyAtbTraits = shallowRef<Record<string, unknown> | null>(null);
  const pageDataStickyAtbFlags = shallowRef<Record<string, unknown> | null>(null);
  const { pageLevelFeatureFlags: { isStickyAtbEnabled = false } = {} } = pageData?.productType?.[pageData?.productId] ?? {};

  const getStickyAtbUUIDSuffix = (id: string | number) => `${id}--${stickyAtbUUID}`;

  const clearAllStickyTraitsInStore = () => {
    Object.keys(traitsStore.$state).forEach((scope) => {
      const traitsData = traitsStore[scope];
      Object.keys(traitsData).forEach((key) => {
        if (key.startsWith(stickyAtbProductId) || key.endsWith(stickyAtbProductId) || key.endsWith(stickyAtbUUID)) {
          traitsStore.set({ scope, key, value: undefined });
        }
      });
    });
  };

  const addEntry = (source: Record<string, unknown>) => {
    return Object.keys(source).reduce((target: Record<string, unknown>, prodId) => {
      target[getStickyAtbUUIDSuffix(prodId)] = {
        ...((source?.[prodId] as Record<string, unknown>) ?? {}),
      };
      return target;
    }, {});
  };

  const updatePageDataEntries = (addStickyEntries: boolean) => {
    if (!addStickyEntries) {
      pageDataStickyAtbTraits.value = null;
      pageDataStickyAtbFlags.value = null;
      // defer computation
      requestAnimationFrame(() => {
        clearAllStickyTraitsInStore();
      });
      return;
    }

    const traitsKeys = Object.keys(pageDataTraits) as Array<keyof typeof pageDataTraits>;
    pageDataStickyAtbTraits.value = traitsKeys.reduce((traitsAcc: Record<string, unknown>, traitKey) => {
      const traitValue = pageDataTraits[traitKey];
      traitsAcc[traitKey] = traitValue && typeof traitValue === 'object' ? addEntry(traitValue as Record<string, unknown>) : traitValue;
      return traitsAcc;
    }, {});

    if (pageDataFlags && typeof pageDataFlags === 'object') {
      pageDataStickyAtbFlags.value = addEntry(pageDataFlags as Record<string, unknown>);
    }
  };

  const triggerForcedRef = () => {
    if (isStickyAtbEnabled) {
      forcedStickyRef.value = Date.now();
    }
  };

  return {
    // STATE
    stickyAtbUUID,
    stickyAtbProductId,
    pageDataStickyAtbTraits,
    pageDataStickyAtbFlags,
    isStickyAtbEnabled,
    forcedStickyRef,

    // ACTIONS
    updatePageDataEntries,
    getStickyAtbUUIDSuffix,
    triggerForcedRef,
  };
});

export default useIsolatedStickyAtbStateStore;
